"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Compass } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 18
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0 : 0.7,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="hero" data-nav="light" className="relative min-h-[90vh] lg:min-h-screen flex items-center justify-center overflow-hidden bg-background text-foreground">
      {/* Background Cinematic Visual with Subtle Editorial Veil */}
      <div className="absolute inset-0 z-0 select-none">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/amalfi_terrace_golden_hour.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/amalfi_terrace_golden_hour.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1796755/img/amalfi_terrace_golden_hour.png-wvc-sizes" alt="Amalfi Coast private villa terrace at golden hour overlooking the Mediterranean Sea" loading="eager" decoding="async" className="w-full h-full object-cover object-center transform scale-105 transition-transform duration-1000 ease-out" />
        {/* Architectural Vignette & Gradient Overlays for optimal readability */}
        <div className="absolute inset-0 bg-background/35" />
        <div className="absolute inset-0 bg-gradient-to-b from-background/55 via-background/20 to-background/60" />
      </div>

      {/* Decorative Orbit Ring Motif */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center opacity-30">
        <div className="h-[480px] w-[480px] sm:h-[640px] sm:w-[640px] rounded-full border border-border/80" />
        <div className="absolute h-[620px] w-[620px] sm:h-[840px] sm:w-[840px] rounded-full border border-border/40" />
      </div>

      {/* Main Content Container */}
      <div className="relative z-10 container mx-auto px-6 sm:px-8 lg:px-12 py-24 sm:py-32 flex flex-col items-center text-center max-w-4xl">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="flex flex-col items-center w-full">
          {/* Eyebrow / Pillar Badge */}
          <motion.div variants={itemVariants} className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-sm bg-muted/80 border border-border/70 mb-8 backdrop-blur-xs shadow-2xs">
            <Compass className="size-3.5 text-primary stroke-[1.25]" />
            <span className="text-xs uppercase tracking-widest font-sans font-medium text-foreground">
              Anchor &amp; Orbit Itineraries
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1 variants={itemVariants} className="font-serif text-3xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground leading-[1.14] text-balance max-w-3xl">
            Travel That Brings Generations Together — In Perfect Harmony
          </motion.h1>

          {/* Hairline Editorial Divider */}
          <motion.div variants={itemVariants} className="flex items-center justify-center gap-3 my-7 w-full max-w-xs">
            <div className="h-px bg-border flex-1" />
            <div className="h-1.5 w-1.5 rotate-45 border border-border bg-background" />
            <div className="h-px bg-border flex-1" />
          </motion.div>

          {/* Subtitle */}
          <motion.p variants={itemVariants} className="font-sans text-base sm:text-lg lg:text-xl text-foreground/90 font-normal max-w-2xl leading-relaxed tracking-normal mb-10 text-balance">
            Ornate luxury travel design for the selective market.
          </motion.p>

          {/* Call to Action */}
          <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 px-8 py-6 text-sm font-sans font-semibold tracking-wider uppercase transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 group">
              <Link to="/AboutDiscoveryConsultationPage">
                <span>Design Your Legacy</span>
                <ArrowRight className="size-4 ml-1.5 transition-transform duration-300 group-hover:translate-x-1 stroke-[1.5]" />
              </Link>
            </Button>
          </motion.div>

          {/* Subtle Framework Meta Tags */}
          <motion.div variants={itemVariants} className="mt-14 pt-6 border-t border-border/60 grid grid-cols-3 gap-6 sm:gap-12 w-full max-w-lg text-center">
            <div>
              <p className="text-[11px] uppercase tracking-wider font-sans font-medium text-foreground/80">
                Pillar I
              </p>
              <p className="text-xs font-serif font-medium text-foreground mt-0.5">
                Family Legacy
              </p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider font-sans font-medium text-foreground/80">
                Pillar II
              </p>
              <p className="text-xs font-serif font-medium text-foreground mt-0.5">
                Generational Harmony
              </p>
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider font-sans font-medium text-foreground/80">
                Pillar III
              </p>
              <p className="text-xs font-serif font-medium text-foreground mt-0.5">
                Logistical Precision
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}